import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import {
  coreContainer,
  coreProductItem,
  exportRequest,
  partyAgency,
} from "@tc/drizzle-v2"
import { CoreLookup, CoreStatus, CoreSubstatus } from "@tc/graphql-server"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, inArray } from "drizzle-orm"
import {
  DBProductItem,
  ProductAttributes,
  TransferSpecificProductDetails,
} from "../types/core"
import { batchedInQuery } from "../utils/batched-in-query"
import { certificateTypeToLabel } from "../utils/certificate-type-mapper"
import { fetchNzhscFromClassification } from "../utils/classification-enrichment"
import { truncateToNzDay } from "../utils/truncate-to-nz-day"
import {
  getCountryInfoBatch,
  getCountryInfoByCode,
  getTemplateInfoBatch,
  getTemplateInfoByVersionId,
} from "../utils/db-helpers"
import { extractProductFields } from "../utils/extract-product-fields"
import { searchBuilderLog } from "../utils/logger"
import { normalizeRequestName } from "../utils/request-name-enrichment"
import { generateStatusLabel } from "../utils/status-label-mapper"
import {
  isTemplateOutdated,
  isTemplateOutdatedBatch,
} from "../utils/template-version-check"

interface ExportCertificateDocument {
  readonly id: string
  readonly party_id: string
  readonly country_id: string
  readonly country_name: string
  readonly request_name: string
  readonly status: string
  readonly status_label: string
  readonly certificate_number: string
  readonly certificate_number_sort: string
  readonly departure_date: string | null
  readonly modified_at: string | null
  readonly modified_ts: number | null
  readonly departure_ts: number | null
  readonly organisation: string
  readonly accessible_by: string[]
  readonly submitted_at: number | null
  readonly approved_at: number | null
  readonly approved_date: string | null
  readonly template_code: string | null
  readonly unique_consignment_ref: string | null
  readonly destination_country: string | null
  readonly destination_country_name: string | null
  readonly local_carrier?: string | null
  readonly carrier_name?: string | null
  readonly conveyance_reference?: string | null
  readonly template_id: string | null
  readonly template_version_id: string | null
  readonly template_version: number | null
  readonly submission_layout: string | null
  readonly transport_mode: string | null
  readonly org_cert_ref_code: string | null
  readonly certificate_sub_type: string | null
  readonly verifying_agency: string | null
  readonly certifying_agency: string | null
  readonly product: {
    id: string
    description: string
    product_botanical_name?: string
    product_common_name?: string
    sub_class?: string
    class?: string
    quantity_amount?: string
    quantity_type?: string
  }[]
  readonly product_descriptions: string[]
  readonly product_net_weights: string[]
  readonly product_net_volumes: string[]
  readonly product_eligibility_countries: string[]
  readonly product_container_numbers: string[]
  readonly product_seals: string[]
  readonly product_NZHSC_codes: string[]
  readonly product_productionBatch_ids: string[]
  readonly product_item_marks: string[]
  readonly product_unofficial_commercial_ref: string[]
  readonly product_unofficial_commercial_info: string[]
  readonly product_botanical_name: string[]
  readonly vessel: string | null
  readonly sub_class: string[] | null
  readonly class: string[] | null
  readonly requires_attention: boolean
  readonly consignor_id: string
  readonly consignee_id: string
  readonly third_party_consignor: string[]
  readonly external_consignor: string | null
  readonly unofficial_information: string | null
  readonly urgency: string | null
  readonly commodity: string | null
  readonly certificate_type: string | null
  readonly template_agencies: string[]
  readonly inspection_port: string | null
  readonly inspection_port_country: string | null
  readonly discharge_port: string | null
  readonly importer_name: string | null
  readonly importer_id: string | null
  readonly oca_access: boolean
}

const client = new SQSClient({})

const getPhytoCertSubType = (attributes: unknown): string | null => {
  if (
    !attributes ||
    typeof attributes !== "object" ||
    !("phytoCertSubType" in attributes)
  ) {
    return null
  }
  const subType = (attributes as { phytoCertSubType?: unknown })
    .phytoCertSubType
  return extractId(subType) || null
}

const getPhytoQuantityType = (attributes: ProductAttributes): string | null => {
  if (
    !attributes["phytoQuantityMeasure"] ||
    typeof attributes["phytoQuantityMeasure"] !== "object"
  ) {
    return null
  }
  const measure = attributes["phytoQuantityMeasure"] as { unit?: unknown }
  return extractId(measure.unit) || null
}

const getOrgCertRefCode = (attributes: unknown): string | null => {
  try {
    return attributes &&
      typeof attributes === "object" &&
      "orgCertRefCode" in attributes
      ? (attributes as { orgCertRefCode?: string }).orgCertRefCode || null
      : null
  } catch (error) {
    console.error("Failed to extract orgCertRefCode:", error)
    return null
  }
}

const getPhytoQuantityValue = (
  attributes: ProductAttributes,
): number | string | null => {
  if (
    attributes["phytoQuantityMeasure"] &&
    typeof attributes["phytoQuantityMeasure"] === "object" &&
    "value" in attributes["phytoQuantityMeasure"]
  ) {
    const value = attributes["phytoQuantityMeasure"].value
    return typeof value === "number" || typeof value === "string" ? value : null
  }
  return null
}

const formatQuantityAmount = (
  value: number | string | null | undefined,
): string | undefined => {
  return typeof value === "number"
    ? value.toString()
    : typeof value === "string"
      ? value
      : undefined
}

const extractId = (obj: unknown): string => {
  return obj && typeof obj === "object" && "id" in obj ? String(obj.id) : ""
}

const extractName = (obj: unknown): string => {
  return obj && typeof obj === "object" && "name" in obj ? String(obj.name) : ""
}

const extractJsonName = (field: unknown): string | null => {
  if (!field) return null
  try {
    const obj = typeof field === "string" ? JSON.parse(field) : field
    return obj?.name || null
  } catch {
    return null
  }
}

/**
 * Generate a sortable version of a certificate number by zero-padding
 * numeric segments so lexicographic sort matches natural numeric order.
 * e.g. "nzl2025/abc/92" → "nzl2025/abc/0000000092"
 */
const toCertificateNumberSort = (certificateNumber: string): string => {
  return certificateNumber
    .split("/")
    .map((segment) =>
      /^\d+$/.test(segment) ? segment.padStart(6, "0") : segment,
    )
    .join("/")
}

/**
 * Update accessible_by field based on party access matrix (User Story 118080)
 * AC2: Consignor can see all exports where they are consignor
 * AC4: 3rd party can see exports in post-submission status only (excludes 'Draft' and 'Resubmit')
 */
const updateAccessibleBy = (row: {
  status: string | null
  consignorId: string | null
  thirdPartyAccess: CoreLookup[] | null
}): string[] => {
  const status = row.status
  const accessible_by: string[] = []

  // AC2: Consignor access - all statuses
  if (row.consignorId) {
    accessible_by.push(row.consignorId)
  }

  const isDraft = status === CoreStatus.Draft
  const isResubmit = status === CoreStatus.Resubmit

  // AC4: 3rd party access - post-submission status only (excludes Draft and Resubmit)
  if (
    !isDraft &&
    !isResubmit &&
    row.thirdPartyAccess &&
    Array.isArray(row.thirdPartyAccess)
  ) {
    row.thirdPartyAccess.forEach((party) => {
      const partyId = extractId(party)
      if (partyId) {
        accessible_by.push(partyId)
      }
    })
  }

  // Remove duplicates and return
  return [...new Set(accessible_by)]
}

/** Bulk fetch — batch queries for a chunk of IDs, returns Map<id, doc> */
export const getExportCertificatesBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, unknown>> => {
  if (ids.length === 0) return new Map()

  // 1. Batch fetch all export request rows
  const rows = await tx
    .select({
      certificateNumber: exportRequest.certificate_number,
      country_id: exportRequest.destination_country,
      id: exportRequest.id,
      modified_at: exportRequest.updatedAt,
      organization: exportRequest.consignorId,
      party_id: exportRequest.consignorId,
      status: exportRequest.status,
      substatus: exportRequest.substatus,
      consignorId: exportRequest.consignorId,
      consigneeId: exportRequest.consigneeId,
      thirdPartyAccess: exportRequest.thirdPartyAccess,
      thirdPartyConsignor: exportRequest.thirdPartyConsignor,
      transport: exportRequest.transport,
      requestorReference: exportRequest.requestorReference,
      submittedAt: exportRequest.submittedAt,
      approvedAt: exportRequest.approvedAt,
      templateCode: exportRequest.templateCode,
      destinationCountry: exportRequest.destination_country,
      productGroupId: exportRequest.productGroupId,
      templateVersionId: exportRequest.templateId,
      templateVersion: exportRequest.templateVersion,
      unofficialInformation: exportRequest.unofficialInformation,
      uniqueConsignmentRef: exportRequest.uniqueConsignmentReference,
      attributes: exportRequest.attributes,
      verifyingAgencyId: exportRequest.verifyingAgencyId,
      certifyingAgencyId: exportRequest.certifyingAgencyId,
      urgency: exportRequest.urgency,
      commodity: exportRequest.commodity,
      certificateType: exportRequest.certificate_type,
      importer: exportRequest.importer,
    })
    .from(exportRequest)
    .where(inArray(exportRequest.id, ids as string[]))

  if (rows.length === 0) return new Map()

  // 2. Batch fetch all product items
  const productGroupIds = rows
    .map((r) => r.productGroupId)
    .filter((id): id is string => id !== null)

  const allProductItems =
    productGroupIds.length > 0
      ? ((await tx
          .select({
            id: coreProductItem.id,
            productGroupId: coreProductItem.productGroupId,
            description: coreProductItem.description,
            attributes: coreProductItem.attributes,
            eligibilityCountries: coreProductItem.eligibilityCountries,
            packaging: coreProductItem.packaging,
            productReference: coreProductItem.productReference,
          })
          .from(coreProductItem)
          .where(
            inArray(coreProductItem.productGroupId, productGroupIds),
          )) as DBProductItem[])
      : []

  const productsByGroup = new Map<string, DBProductItem[]>()
  for (const p of allProductItems) {
    const pgId = p.productGroupId ?? ""
    const group = productsByGroup.get(pgId) ?? []
    group.push(p)
    productsByGroup.set(pgId, group)
  }

  // 3. Batch fetch all containers (batched to avoid seq scan on 30M+ row table)
  const allProductItemIds = allProductItems.map((p) => p.id)
  const allContainers = await batchedInQuery<{
    productItemId: string | null
    containerNumber: string | null
    seals: unknown
  }>(tx, coreContainer, coreContainer.productItemId, allProductItemIds, {
    productItemId: coreContainer.productItemId,
    containerNumber: coreContainer.containerNumber,
    seals: coreContainer.seals,
  })

  const containersByProduct = new Map<
    string,
    { containerNumber: string | null; seals: unknown[] }
  >()
  for (const c of allContainers) {
    if (c.productItemId)
      containersByProduct.set(c.productItemId, {
        containerNumber: c.containerNumber,
        seals: Array.isArray(c.seals) ? c.seals : [],
      })
  }

  // 4. Batch fetch NZHSCode from classification table
  const nzhscByProduct = await fetchNzhscFromClassification(
    tx,
    allProductItemIds,
  )

  // 5. Batch fetch template info + outdated check (3 queries total instead of 3×N)
  const templateVersionIds = [
    ...new Set(
      rows
        .map((r) => r.templateVersionId)
        .filter((id): id is string => id !== null),
    ),
  ]
  const [templateInfoMap, templateOutdatedMap] = await Promise.all([
    getTemplateInfoBatch(tx, templateVersionIds),
    isTemplateOutdatedBatch(tx, templateVersionIds),
  ])

  // 5. Batch fetch verifying agency identifiers
  const verifyingAgencyIds = [
    ...new Set(
      rows
        .map((r) => r.verifyingAgencyId)
        .filter((id): id is string => id !== null),
    ),
  ]
  const verifyingAgencyMap = new Map<string, string | null>()
  if (verifyingAgencyIds.length > 0) {
    const agencies = await tx
      .select({ id: partyAgency.id, identifier: partyAgency.identifier })
      .from(partyAgency)
      .where(inArray(partyAgency.id, verifyingAgencyIds))
    for (const a of agencies) verifyingAgencyMap.set(a.id, a.identifier)
  }

  // 5b. Batch fetch certifying agency identifiers
  const certifyingAgencyIds = [
    ...new Set(
      rows
        .map((r) => r.certifyingAgencyId)
        .filter((id): id is string => id !== null),
    ),
  ]
  const certifyingAgencyMap = new Map<string, string | null>()
  if (certifyingAgencyIds.length > 0) {
    const agencies = await tx
      .select({ id: partyAgency.id, identifier: partyAgency.identifier })
      .from(partyAgency)
      .where(inArray(partyAgency.id, certifyingAgencyIds))
    for (const a of agencies) certifyingAgencyMap.set(a.id, a.identifier)
  }

  // 6. Batch fetch destination country names (1 query instead of N)
  const countryCodes = [
    ...new Set(
      rows.map((r) => extractId(r.destinationCountry)).filter(Boolean),
    ),
  ]
  const countryNameMap = await getCountryInfoBatch(tx, countryCodes)

  // 7. Build docs per row
  const result = new Map<string, unknown>()

  for (const row of rows) {
    const products = row.productGroupId
      ? (productsByGroup.get(row.productGroupId) ?? [])
      : []

    let transportMode: string | null = null
    let departure_ts: number | null = null
    let localCarrier: string | null = null
    let carrierName: string | null = null
    let conveyanceReference: string | null = null
    let inspectionPort: string | null = null
    let dischargePort: string | null = null
    let importerName: string | null = null
    let importerId: string | null = null

    if (row.transport) {
      try {
        const transportObj =
          typeof row.transport === "string"
            ? JSON.parse(row.transport)
            : row.transport
        const raw = transportObj?.departureDate
        transportMode = extractName(transportObj?.transportMode)
        localCarrier = transportObj?.localCarrier
        carrierName = transportObj?.carrierName
        conveyanceReference = transportObj?.conveyanceReference
        inspectionPort = transportObj?.inspectionPort?.id || null
        dischargePort = transportObj?.dischargePort?.id || null
        if (typeof raw === "string" && raw.trim() !== "") {
          const date = new Date(raw)
          departure_ts = isNaN(date.getTime())
            ? null
            : truncateToNzDay(date.getTime())
        } else if (typeof raw === "number") {
          departure_ts = Number.isFinite(raw) ? truncateToNzDay(raw) : null
        }
      } catch {
        /* skip */
      }
    }
    if (row?.importer) {
      const importerObj =
        typeof row.importer === "string"
          ? JSON.parse(row.importer)
          : row.importer
      importerId = importerObj?.id || null
      importerName = importerObj?.name || null
    }
    const productsForDocument: TransferSpecificProductDetails[] = products.map(
      (product) => {
        const attributes: ProductAttributes =
          product.attributes && typeof product.attributes === "object"
            ? product.attributes
            : {}
        // Enrich with NZHSCode from classification table if not already present
        if (
          !attributes.NZHSCode &&
          product?.id &&
          nzhscByProduct.has(product.id)
        ) {
          attributes.NZHSCode = nzhscByProduct.get(
            product.id,
          ) as unknown as ProductAttributes["NZHSCode"]
        }
        const netWeight = attributes.netWeight || null
        const netVolume = attributes.netVolume || null
        const netWeightUnit =
          netWeight && typeof netWeight === "object" && netWeight.unit
            ? extractId(netWeight.unit)
            : ""
        const netVolumeUnit =
          netVolume && typeof netVolume === "object" && netVolume.unit
            ? extractId(netVolume.unit)
            : ""

        const NZHSC_id = extractId(attributes?.NZHSCode)
        const NZHSCode = `${extractName(attributes?.NZHSCode)} (${NZHSC_id})`
        const productionBatchIds = Array.isArray(attributes.productionBatchIds)
          ? attributes.productionBatchIds
          : []
        const itemMarks = Array.isArray(product.packaging)
          ? (product.packaging as Array<{ itemMarksOuter?: string | null }>)
              .map((pkg) => pkg?.itemMarksOuter)
              .filter((m): m is string => Boolean(m) && typeof m === "string")
          : []
        const subClass = extractName(attributes.phytoSubClass)
        const phytoClass = extractName(attributes.phytoClass)
        const productStatement =
          typeof attributes["productStatement"] === "string"
            ? attributes["productStatement"]
            : null
        const botanicalName = extractName(attributes.phytoBotanicalName)
        const commonName = extractName(attributes["phytoCommonName"])
        const phytoQuantityType = getPhytoQuantityType(attributes)
        const phytoQuantityValue = getPhytoQuantityValue(attributes)
        const container = containersByProduct.get(product.id || "")

        return {
          id: product.id,
          description: extractName(product.description),
          net_weight:
            netWeight && typeof netWeight === "object" && netWeight.value
              ? `${netWeight.value} ${netWeightUnit}`.trim()
              : null,
          net_volume:
            netVolume && typeof netVolume === "object" && netVolume.value
              ? `${netVolume.value} ${netVolumeUnit}`.trim()
              : null,
          eligibility_countries: Array.isArray(product.eligibilityCountries)
            ? product.eligibilityCountries
                .map((c: unknown) => {
                  const id = extractId(c)
                  const name = extractName(c)
                  if (id && name) return `${id} - ${name}`
                  return id || name || ""
                })
                .filter(Boolean)
            : [],
          container_number: container?.containerNumber || null,
          seals: Array.isArray(container?.seals)
            ? (container.seals as string[])
            : [],
          NZHSC_code: NZHSCode,
          productionBatch_ids: productionBatchIds,
          class: phytoClass,
          sub_class: subClass,
          unofficial_commercial_ref:
            typeof product.productReference === "string"
              ? product.productReference
              : null,
          unofficial_commercial_info: productStatement,
          item_marks: itemMarks,
          botanical_name: botanicalName,
          common_name: commonName,
          quantity_type: phytoQuantityType,
          quantity_value: phytoQuantityValue,
        }
      },
    )

    const templateInfo = row.templateVersionId
      ? templateInfoMap.get(row.templateVersionId)
      : undefined
    const templateId = templateInfo?.templateId ?? null
    const submissionLayout = templateInfo?.submissionLayout ?? null
    const templateAgencies = templateInfo?.agencies ?? []
    const ocaAccess = templateInfo?.ocaAccess ?? false
    const templateOutdated = row.templateVersionId
      ? (templateOutdatedMap.get(row.templateVersionId) ?? false)
      : false
    const requiresAttention =
      templateOutdated &&
      ["RAISED", "RESUBMIT", "AMENDED"].includes(row.status || "")
    const verifyingAgency = row.verifyingAgencyId
      ? (verifyingAgencyMap.get(row.verifyingAgencyId) ?? null)
      : null
    const certifyingAgency = row.certifyingAgencyId
      ? (certifyingAgencyMap.get(row.certifyingAgencyId) ?? null)
      : null
    const destinationCountryName = extractId(row.destinationCountry)
      ? (countryNameMap.get(extractId(row.destinationCountry)) ?? null)
      : null

    let externalConsignorName: string | null = null
    if (
      row.thirdPartyConsignor &&
      typeof row.thirdPartyConsignor === "object" &&
      !Array.isArray(row.thirdPartyConsignor)
    ) {
      try {
        const obj =
          typeof row.thirdPartyConsignor === "string"
            ? JSON.parse(row.thirdPartyConsignor)
            : row.thirdPartyConsignor
        externalConsignorName = obj?.name || null
      } catch {
        /* skip */
      }
    }

    const productFields = extractProductFields(productsForDocument)

    const urgencyName = extractJsonName(row.urgency)

    const document: ExportCertificateDocument = {
      id: row.id,
      party_id: extractId(row.party_id),
      country_id: extractId(row.destinationCountry),
      country_name: destinationCountryName ?? "",
      request_name: normalizeRequestName(
        row.requestorReference ?? "",
        row.commodity,
      ),
      consignor_id: extractId(row.consignorId),
      consignee_id: extractId(row.consigneeId),
      third_party_consignor: Array.isArray(row.thirdPartyConsignor)
        ? row.thirdPartyConsignor.map(extractId).filter(Boolean)
        : extractId(row.thirdPartyConsignor)
          ? [extractId(row.thirdPartyConsignor)]
          : [],
      external_consignor: externalConsignorName,
      status: row.status ?? "",
      status_label: generateStatusLabel(
        row.status as CoreStatus,
        row.substatus as CoreSubstatus,
      ),
      certificate_number: row.certificateNumber ?? "",
      certificate_number_sort: toCertificateNumberSort(
        row.certificateNumber ?? "",
      ),
      departure_date:
        departure_ts === null
          ? ""
          : new Date(departure_ts).toString().replace(/\s*\(.*\)$/, ""),
      departure_ts: departure_ts ?? null,
      modified_ts: row.modified_at
        ? truncateToNzDay(new Date(row.modified_at.toString()).getTime())
        : null,
      modified_at: row.modified_at.toString().replace(/\s*\(.*\)$/, "") ?? "",
      organisation: extractId(row.organization),
      accessible_by: updateAccessibleBy({
        status: row.status,
        consignorId: extractId(row.consignorId),
        thirdPartyAccess: row.thirdPartyAccess as CoreLookup[] | null,
      }),
      submitted_at: row.submittedAt
        ? truncateToNzDay(new Date(row.submittedAt.toString()).getTime())
        : null,
      approved_at: row.approvedAt
        ? truncateToNzDay(new Date(row.approvedAt.toString()).getTime())
        : null,
      approved_date:
        row.approvedAt === null
          ? null
          : new Date(row.approvedAt).toString().replace(/\s*\(.*\)$/, ""),
      template_code: row.templateCode ?? null,
      destination_country: extractId(row.destinationCountry),
      destination_country_name: destinationCountryName ?? null,
      local_carrier: localCarrier,
      carrier_name: carrierName,
      conveyance_reference: conveyanceReference,
      template_id: templateId,
      template_version_id: row.templateVersionId ?? null,
      template_version: row.templateVersion ?? null,
      submission_layout: submissionLayout,
      transport_mode: transportMode ?? null,
      org_cert_ref_code: getOrgCertRefCode(row.attributes) ?? "",
      certificate_sub_type: getPhytoCertSubType(row.attributes),
      verifying_agency: verifyingAgency,
      certifying_agency: certifyingAgency,
      urgency: urgencyName,
      commodity: row.commodity ?? null,
      certificate_type: certificateTypeToLabel(row.certificateType),
      product:
        submissionLayout === "PHYTO_EXPORT"
          ? (productsForDocument
              .map((product) => ({
                id: product.id || "",
                description: product.description || "",
                product_botanical_name: product.botanical_name || undefined,
                product_common_name: product.common_name || undefined,
                sub_class: product.sub_class || undefined,
                class: product.class || undefined,
                quantity_type: product.quantity_type || undefined,
                quantity_amount: formatQuantityAmount(product.quantity_value),
              }))
              .filter(
                (p) =>
                  p.product_botanical_name ||
                  p.product_common_name ||
                  p.sub_class ||
                  p.class ||
                  p.quantity_amount ||
                  p.quantity_type,
              ) ?? [])
          : productsForDocument.map((product) => ({
              id: product.id || "",
              description: product.description || "",
              product_botanical_name: product.botanical_name || undefined,
              product_common_name: product.common_name || undefined,
              sub_class: product.sub_class || undefined,
              class: product.class || undefined,
              quantity_type: product.quantity_type || undefined,
              quantity_amount: formatQuantityAmount(product.quantity_value),
            })),
      product_descriptions: productFields.descriptions,
      product_net_weights: productFields.net_weights,
      product_net_volumes: productFields.net_volumes,
      product_container_numbers: productFields.container_numbers,
      product_NZHSC_codes: productFields.NZHSC_codes,
      product_eligibility_countries: productFields.eligibility_countries,
      product_seals: productFields.seals,
      product_productionBatch_ids: productFields.production_batch_ids,
      product_item_marks: productFields.item_marks,
      product_unofficial_commercial_ref:
        productFields.unofficial_commercial_ref,
      product_unofficial_commercial_info:
        productFields.unofficial_commercial_info,
      product_botanical_name: productFields.botanical_name,
      vessel: carrierName,
      unique_consignment_ref: row.uniqueConsignmentRef ?? null,
      unofficial_information: row.unofficialInformation ?? null,
      class: productFields.class,
      sub_class: productFields.sub_class,
      requires_attention: requiresAttention,
      template_agencies: templateAgencies,
      inspection_port: inspectionPort,
      inspection_port_country: inspectionPort
        ? inspectionPort.substring(0, 2)
        : null,
      discharge_port: dischargePort,
      importer_name: importerName,
      importer_id: importerId,
      oca_access: ocaAccess,
    }

    result.set(row.id, document)
  }

  return result
}

export const saveExportCertificatesToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName?: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  searchBuilderLog.debug({
    message:
      "Preparing message to send with Export Certificate Details to Search Service",
    collection: collectionName,
    id: id,
  })

  const [row] = await tx
    .select({
      certificateNumber: exportRequest.certificate_number,
      country_id: exportRequest.destination_country,
      id: exportRequest.id,
      modified_at: exportRequest.updatedAt,
      organization: exportRequest.consignorId,
      party_id: exportRequest.consignorId,
      status: exportRequest.status,
      substatus: exportRequest.substatus,
      consignorId: exportRequest.consignorId,
      consigneeId: exportRequest.consigneeId,
      thirdPartyAccess: exportRequest.thirdPartyAccess,
      thirdPartyConsignor: exportRequest.thirdPartyConsignor,
      transport: exportRequest.transport,
      requestorReference: exportRequest.requestorReference,
      submittedAt: exportRequest.submittedAt,
      approvedAt: exportRequest.approvedAt,
      templateCode: exportRequest.templateCode,
      destinationCountry: exportRequest.destination_country,
      productGroupId: exportRequest.productGroupId,
      templateVersionId: exportRequest.templateId,
      templateVersion: exportRequest.templateVersion,
      unofficialInformation: exportRequest.unofficialInformation,
      uniqueConsignmentRef: exportRequest.uniqueConsignmentReference,
      attributes: exportRequest.attributes,
      verifyingAgencyId: exportRequest.verifyingAgencyId,
      certifyingAgencyId: exportRequest.certifyingAgencyId,
      urgency: exportRequest.urgency,
      commodity: exportRequest.commodity,
      certificateType: exportRequest.certificate_type,
      importer: exportRequest.importer,
    })
    .from(exportRequest)
    .where(eq(exportRequest.id, id))

  if (!row) {
    console.error(`Export request with the id ${id} not found`)
    return
  }
  let transportMode: string | null = null
  let departure_ts: number | null = null
  if (row.transport) {
    try {
      const transportObj =
        typeof row.transport === "string"
          ? JSON.parse(row.transport)
          : row.transport

      const raw = transportObj?.departureDate
      transportMode = extractName(transportObj?.transportMode)
      if (typeof raw === "string" && raw.trim() !== "") {
        const date = new Date(raw)
        departure_ts = isNaN(date.getTime())
          ? null
          : truncateToNzDay(date.getTime())
      } else if (typeof raw === "number") {
        departure_ts = Number.isFinite(raw) ? truncateToNzDay(raw) : null
      } else {
        departure_ts = null
      }
    } catch (e) {
      console.error("Failed to parse transport JSON", e)
      departure_ts = null
    }
  }

  let productsForDocument: TransferSpecificProductDetails[] = []
  if (row.productGroupId) {
    const productItems = await tx
      .select({
        id: coreProductItem.id,
        description: coreProductItem.description,
        attributes: coreProductItem.attributes,
        eligibilityCountries: coreProductItem.eligibilityCountries,
        packaging: coreProductItem.packaging,
        productReference: coreProductItem.productReference,
      })
      .from(coreProductItem)
      .where(eq(coreProductItem.productGroupId, row.productGroupId))

    const typedProductItems = productItems as DBProductItem[]

    // Fetch classifications for NZHSCode enrichment
    const productItemIds = typedProductItems.map((p) => p.id)
    const singleRowNzhsc = await fetchNzhscFromClassification(
      tx,
      productItemIds,
    )

    const getContainerDetails = async (productId: string) => {
      const [container] = await tx
        .select({
          containerNumber: coreContainer.containerNumber,
          seals: coreContainer.seals,
        })
        .from(coreContainer)
        .where(eq(coreContainer.productItemId, productId))
      return {
        containerNumber: container.containerNumber || null,
        seals: Array.isArray(container.seals) ? container.seals : [],
      }
    }

    const getItemMarks = (packaging: unknown): string[] => {
      if (!Array.isArray(packaging)) {
        return []
      }

      return packaging
        .map((pkg) => pkg?.itemMarksOuter)
        .filter(
          (mark): mark is string => Boolean(mark) && typeof mark === "string",
        )
    }

    if (typedProductItems.length > 0) {
      productsForDocument = await Promise.all(
        typedProductItems.map(
          async (product): Promise<TransferSpecificProductDetails> => {
            const attributes: ProductAttributes =
              product.attributes && typeof product.attributes === "object"
                ? product.attributes
                : {}
            // Enrich with NZHSCode from classification table if not already present
            if (
              !attributes.NZHSCode &&
              product?.id &&
              singleRowNzhsc.has(product.id)
            ) {
              attributes.NZHSCode = singleRowNzhsc.get(
                product.id,
              ) as unknown as ProductAttributes["NZHSCode"]
            }
            const netWeight = attributes.netWeight || null
            const netVolume = attributes.netVolume || null
            const netWeightUnit =
              netWeight && typeof netWeight === "object" && netWeight.unit
                ? extractId(netWeight.unit)
                : ""
            const netVolumeUnit =
              netVolume && typeof netVolume === "object" && netVolume.unit
                ? extractId(netVolume.unit)
                : ""

            const NZHSCode = extractId(attributes.NZHSCode) || null
            const productionBatchIds = Array.isArray(
              attributes.productionBatchIds,
            )
              ? attributes.productionBatchIds
              : []
            const itemMarks: string[] = getItemMarks(product.packaging)
            const subClass = extractName(attributes.phytoSubClass)
            const phytoClass = extractName(attributes.phytoClass)
            const productStatement =
              typeof attributes["productStatement"] === "string"
                ? attributes["productStatement"]
                : null
            const botanicalName = extractName(attributes.phytoBotanicalName)
            const commonName = extractName(attributes["phytoCommonName"])
            const phytoQuantityType = getPhytoQuantityType(attributes)
            const phytoQuantityValue = getPhytoQuantityValue(attributes)

            let containerNumber: string | null = null
            let seals: string[] = []

            try {
              const containerDetails = await getContainerDetails(
                product.id || "",
              )
              containerNumber = containerDetails.containerNumber || null
              seals = containerDetails.seals || []
            } catch (error) {
              console.error(
                "Error getting container details for product",
                product.id,
                error,
              )
            }

            return {
              id: product.id,
              description: extractName(product.description),
              net_weight:
                netWeight && typeof netWeight === "object" && netWeight.value
                  ? `${netWeight.value} ${netWeightUnit}`.trim()
                  : null,
              net_volume:
                netVolume && typeof netVolume === "object" && netVolume.value
                  ? `${netVolume.value} ${netVolumeUnit}`.trim()
                  : null,
              eligibility_countries: Array.isArray(product.eligibilityCountries)
                ? product.eligibilityCountries
                    .map((c: unknown) => {
                      const id = extractId(c)
                      const name = extractName(c)
                      if (id && name) return `${id} - ${name}`
                      return id || name || ""
                    })
                    .filter(Boolean)
                : [],
              container_number: containerNumber,
              seals: seals || [],
              NZHSC_code: NZHSCode,
              productionBatch_ids: productionBatchIds,
              class: phytoClass,
              sub_class: subClass,
              unofficial_commercial_ref:
                typeof product.productReference === "string"
                  ? product.productReference
                  : null,
              unofficial_commercial_info: productStatement,
              item_marks: itemMarks,
              botanical_name: botanicalName,
              common_name: commonName,
              quantity_type: phytoQuantityType,
              quantity_value: phytoQuantityValue,
            }
          },
        ),
      )
    }
  }

  let localCarrier: string | null = null
  let carrierName: string | null = null
  let conveyanceReference: string | null = null
  let inspectionPort: string | null = null
  let dischargePort: string | null = null
  let importerName: string | null = null
  let importerId: string | null = null

  if (row.transport) {
    try {
      const transportObj =
        typeof row.transport === "string"
          ? JSON.parse(row.transport)
          : row.transport

      localCarrier = transportObj?.localCarrier
      carrierName = transportObj?.carrierName
      conveyanceReference = transportObj?.conveyanceReference
      inspectionPort = transportObj?.inspectionPort?.id || null
      dischargePort = transportObj?.dischargePort?.id || null
    } catch (e) {
      console.error("Failed to parse transport JSON", e)
    }
  }
  if (row?.importer) {
    const importerObj =
      typeof row.importer === "string" ? JSON.parse(row.importer) : row.importer
    importerId = importerObj?.id || null
    importerName = importerObj?.name || null
  }

  let templateId: string | null = null
  let submissionLayout: string | null = null
  let templateAgencies: string[] = []
  let ocaAccess = false

  if (row.templateVersionId) {
    try {
      const templateInfo = await getTemplateInfoByVersionId(
        tx,
        row.templateVersionId,
      )
      templateId = templateInfo.templateId
      submissionLayout = templateInfo.submissionLayout
      templateAgencies = templateInfo.agencies
      ocaAccess = templateInfo.ocaAccess
    } catch (error) {
      console.error("Failed to fetch template info:", error)
      templateId = null
      submissionLayout = null
    }
  }

  const productFields = extractProductFields(productsForDocument)

  // Extract third party consignor name from thirdPartyConsignor (B2G PHYTO data)
  let externalConsignorName: string | null = null
  if (
    row.thirdPartyConsignor &&
    typeof row.thirdPartyConsignor === "object" &&
    !Array.isArray(row.thirdPartyConsignor)
  ) {
    try {
      const thirdPartyConsignorObj =
        typeof row.thirdPartyConsignor === "string"
          ? JSON.parse(row.thirdPartyConsignor)
          : row.thirdPartyConsignor
      externalConsignorName = thirdPartyConsignorObj?.name || null
    } catch (e) {
      console.error("Failed to parse thirdPartyConsignor JSON", e)
      externalConsignorName = null
    }
  }

  // Fetch verifying agency identifier
  let verifyingAgency: string | null = null
  if (row.verifyingAgencyId) {
    try {
      const [agency] = await tx
        .select({ identifier: partyAgency.identifier })
        .from(partyAgency)
        .where(eq(partyAgency.id, row.verifyingAgencyId))
      verifyingAgency = agency.identifier || null
    } catch (error) {
      console.error("Failed to fetch verifying agency:", error)
      verifyingAgency = null
    }
  }

  // Fetch certifying agency identifier
  let certifyingAgency: string | null = null
  if (row.certifyingAgencyId) {
    try {
      const [agency] = await tx
        .select({ identifier: partyAgency.identifier })
        .from(partyAgency)
        .where(eq(partyAgency.id, row.certifyingAgencyId))
      certifyingAgency = agency.identifier || null
    } catch (error) {
      console.error("Failed to fetch certifying agency:", error)
      certifyingAgency = null
    }
  }

  // Check if request requires attention
  const templateOutdated = row.templateVersionId
    ? await isTemplateOutdated(tx, row.templateVersionId)
    : false
  const requiresAttention =
    templateOutdated &&
    ["RAISED", "RESUBMIT", "AMENDED"].includes(row.status || "")

  // Fetch destination country name
  let destinationCountryName: string | null = null
  if (row.destinationCountry) {
    try {
      const countryData = await getCountryInfoByCode(
        tx,
        extractId(row.destinationCountry),
      )
      destinationCountryName = countryData[0]?.itemName || null
    } catch (error) {
      console.error("Failed to fetch country name:", error)
      destinationCountryName = null
    }
  }

  const document: ExportCertificateDocument = {
    id: id,
    party_id: extractId(row.party_id),
    country_id: extractId(row.destinationCountry),
    country_name: destinationCountryName ?? "", // Placeholder, as country_name is not in the exportRequest schema
    request_name: normalizeRequestName(
      row.requestorReference ?? "",
      row.commodity,
    ), // Placeholder, as request_name is not in the exportRequest schema
    consignor_id: extractId(row.consignorId),
    consignee_id: extractId(row.consigneeId),
    third_party_consignor: Array.isArray(row.thirdPartyConsignor)
      ? row.thirdPartyConsignor.map(extractId).filter(Boolean)
      : extractId(row.thirdPartyConsignor)
        ? [extractId(row.thirdPartyConsignor)]
        : [],
    external_consignor: externalConsignorName,
    status: row.status ?? "",
    status_label: generateStatusLabel(
      row.status as CoreStatus,
      row.substatus as CoreSubstatus,
    ),
    certificate_number: row.certificateNumber ?? "",
    certificate_number_sort: toCertificateNumberSort(
      row.certificateNumber ?? "",
    ),
    departure_date:
      departure_ts === null
        ? ""
        : new Date(departure_ts).toString().replace(/\s*\(.*\)$/, ""),
    departure_ts: departure_ts ?? null,
    modified_ts: row.modified_at
      ? truncateToNzDay(new Date(row.modified_at.toString()).getTime())
      : null,
    modified_at: row.modified_at.toString().replace(/\s*\(.*\)$/, "") ?? "",
    organisation: extractId(row.organization),
    accessible_by: updateAccessibleBy({
      status: row.status,
      consignorId: extractId(row.consignorId),
      thirdPartyAccess: row.thirdPartyAccess as CoreLookup[] | null,
    }),
    submitted_at: row.submittedAt
      ? truncateToNzDay(new Date(row.submittedAt.toString()).getTime())
      : null,
    approved_at: row.approvedAt
      ? truncateToNzDay(new Date(row.approvedAt.toString()).getTime())
      : null,
    approved_date:
      row.approvedAt === null
        ? null
        : new Date(row.approvedAt).toString().replace(/\s*\(.*\)$/, ""),
    template_code: row.templateCode ?? null,
    destination_country: extractId(row.destinationCountry),
    destination_country_name: destinationCountryName ?? null,
    local_carrier: localCarrier,
    carrier_name: carrierName,
    conveyance_reference: conveyanceReference,
    template_id: templateId ?? null,
    template_version_id: row.templateVersionId ?? null,
    template_version: row.templateVersion ?? null,
    submission_layout: submissionLayout ?? null,
    transport_mode: transportMode ?? null,
    org_cert_ref_code: getOrgCertRefCode(row.attributes) ?? "",
    certificate_sub_type: getPhytoCertSubType(row.attributes),
    verifying_agency: verifyingAgency,
    certifying_agency: certifyingAgency,
    urgency: extractJsonName(row.urgency),
    commodity: row.commodity ?? null,
    certificate_type: certificateTypeToLabel(row.certificateType),
    product:
      submissionLayout === "PHYTO_EXPORT"
        ? (productsForDocument
            .map((product) => ({
              id: product.id || "",
              description: product.description || "",
              product_botanical_name: product.botanical_name || undefined,
              product_common_name: product.common_name || undefined,
              sub_class: product.sub_class || undefined,
              class: product.class || undefined,
              quantity_type: product.quantity_type || undefined,
              quantity_amount: formatQuantityAmount(product.quantity_value),
            }))
            .filter(
              (product) =>
                product.product_botanical_name ||
                product.product_common_name ||
                product.sub_class ||
                product.class ||
                product.quantity_amount ||
                product.quantity_type,
            ) ?? [])
        : productsForDocument.map((product) => ({
            id: product.id || "",
            description: product.description || "",
            product_botanical_name: product.botanical_name || undefined,
            product_common_name: product.common_name || undefined,
            sub_class: product.sub_class || undefined,
            class: product.class || undefined,
            quantity_type: product.quantity_type || undefined,
            quantity_amount: formatQuantityAmount(product.quantity_value),
          })),
    product_descriptions: productFields.descriptions,
    product_net_weights: productFields.net_weights,
    product_net_volumes: productFields.net_volumes,
    product_container_numbers: productFields.container_numbers,
    product_NZHSC_codes: productFields.NZHSC_codes,
    product_eligibility_countries: productFields.eligibility_countries,
    product_seals: productFields.seals,
    product_productionBatch_ids: productFields.production_batch_ids,
    product_item_marks: productFields.item_marks,
    product_unofficial_commercial_ref: productFields.unofficial_commercial_ref,
    product_unofficial_commercial_info:
      productFields.unofficial_commercial_info,
    product_botanical_name: productFields.botanical_name,
    vessel: carrierName,
    unique_consignment_ref: row.uniqueConsignmentRef ?? null,
    unofficial_information: row.unofficialInformation ?? null,
    class: productFields.class,
    sub_class: productFields.sub_class,
    requires_attention: requiresAttention,
    template_agencies: templateAgencies,
    inspection_port: inspectionPort,
    inspection_port_country: inspectionPort
      ? inspectionPort.substring(0, 2)
      : null,
    discharge_port: dischargePort,
    importer_name: importerName,
    importer_id: importerId,
    oca_access: ocaAccess,
  }

  if (options?.returnDoc) return document as never

  searchBuilderLog.debug({
    message:
      "Sending message with retrieved Export Certificate Details to Search service via SQS",
    exportCertificateId: row.id,
    exportCertificateNumber: row.certificateNumber,
    collection: collectionName,
    document: document,
  })

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName ?? process.env["COLLECTION_NAME"],
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: process.env["QUEUE_URL"],
    MessageBody: JSON.stringify(document),
    MessageAttributes: attributes,
  })

  try {
    await client.send(command)
  } catch (error) {
    console.error("Error sending export certificates to search:", error)
  }
  searchBuilderLog.info({
    message:
      "Successfully sent message with retrieved Export Certificate Details to Search service via SQS",
    exportCertificateId: row.id,
    exportCertificateNumber: row.certificateNumber,
    collection: collectionName,
  })
}
